import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MapPinIcon, MenuIcon, PhoneIcon, SearchIcon, ShoppingCartIcon } from 'lucide-react';
import { business, images, navigation, routes } from '../data/site';
import { MobileMenu } from './MobileMenu';
import { SiteLink } from './SiteLink';
export function Header() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenIndex(null);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenIndex(null);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  return <>
      <div className="w-full bg-ink" data-id="element-176" data-mp="2rlrhramv5eyu">
        <div className="mx-auto flex max-w-container flex-col gap-1 px-5 py-2 sm:px-8 lg:flex-row lg:items-center lg:justify-between" data-id="element-177" data-mp="2rkq5x3y0wog2">
          <p className="text-[13.5px] leading-5 text-white sm:text-[14px]" data-id="element-178" data-mp="2mmx0we8gi0hd">{business.localBar}</p>
          <div className="hidden shrink-0 items-center gap-5 lg:flex" data-id="element-179" data-mp="2hpeouez57555">
            <a href={business.phoneHref} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-white transition-colors duration-150 ease-out hover:text-water" data-id="element-180" data-mp="3faasrxxas5um">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="35jpuycpg75cr" />
              Call or text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-white transition-colors duration-150 ease-out hover:text-water" data-id="element-182" data-mp="30kljtgiyqxjs">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-183" data-mp="1rd1jgtpy4hib" />
              Directions
            </SiteLink>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 w-full border-b border-ink/10 bg-white shadow-[0_1px_3px_rgba(20,33,61,0.08)]" data-id="element-184" data-mp="1wbuvsithy2gw">
        <div className="mx-auto flex max-w-container items-center justify-between gap-4 px-5 py-2 sm:px-8" data-id="element-185" data-mp="1mmux70iwp938">
          <SiteLink to={routes.home} className="shrink-0" aria-label="Pegasus Spas and Leisure Products home" data-id="element-186" data-mp="18nbpg8u0bzrg">
            <img src={images.logo} alt="Pegasus Spas & Leisure Products, since 1977" className="h-[52px] w-auto md:h-[60px] xl:h-[76px]" width={352} height={140} data-id="element-187" data-mp="1cq00mn671irl" />
          </SiteLink>

          <div ref={navRef} className="hidden flex-1 items-center justify-end gap-3 xl:flex xl:gap-6" data-id="element-188" data-mp="2t2ncnio97yjq">
            <nav aria-label="Main" data-id="element-189" data-mp="2y0yrk7teoz8t">
              <ul className="flex flex-nowrap items-center gap-1" data-id="element-190" data-mp="2xzxdduh3fwmv">
                {navigation.map((item, index) => {
                const isOpen = openIndex === index;
                const panelId = `nav-panel-${index}`;
                return <li key={item.label} className="relative" onMouseEnter={() => item.children && setOpenIndex(index)} onMouseLeave={() => item.children && setOpenIndex(null)} data-id="element-191" data-mp="w5j4n94ja7oa" data-mpi={index}>
                      <div className="flex items-center" data-id="element-192" data-mp="106uloq9cdkkv">
                        <SiteLink to={item.href} className="whitespace-nowrap rounded-btn px-1.5 py-2 text-[15px] font-medium text-ink transition-colors duration-150 ease-out hover:text-pegasus-blue xl:px-2 xl:text-[15.5px]" data-id="element-193" data-mp="108fgmgzkj5j9">
                          {item.label}
                        </SiteLink>
                        {item.children && <button type="button" aria-expanded={isOpen} aria-controls={panelId} aria-label={`${item.label} menu`} onClick={() => setOpenIndex(isOpen ? null : index)} className="rounded-btn p-0.5 text-ink transition-colors duration-150 ease-out hover:text-pegasus-magenta xl:p-1" data-id="element-194" data-mp="22yxdpga47en5">
                            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-195" data-mp="1p7a82pua8trp" />
                          </button>}
                      </div>

                      {item.children && <div id={panelId} hidden={!isOpen} className={`absolute top-full z-50 w-64 rounded-card border border-ink/10 bg-white py-2 shadow-lg ${index === navigation.length - 1 ? 'right-0' : 'left-0'}`} data-id="element-196" data-mp="1y041rsah7vp3">
                          <ul data-id="element-197" data-mp="xypfmemwumn8">
                            {item.children.map(child => <li key={child.label} data-id="element-198" data-mp="t1qlvx75shpk">
                                <SiteLink to={child.href} className="block px-4 py-2 text-[15px] text-ink transition-colors duration-150 ease-out hover:bg-water hover:text-pegasus-blue" data-id="element-199" data-mp="syuqafisa54a">
                                  {child.label}
                                </SiteLink>
                              </li>)}
                          </ul>
                        </div>}
                    </li>;
              })}
              </ul>
            </nav>

            <div className="flex items-center gap-1 border-l border-ink/10 pl-3 xl:gap-2 xl:pl-4" data-id="element-200" data-mp="3sfj4re2ch54f">
              <button type="button" title="Search" aria-label="Search" className="rounded-btn p-2 text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-201" data-mp="3ng69c4xeqpq8">
                <SearchIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" data-mp="3itrols4u9s14" />
              </button>
              <button type="button" title="Cart" aria-label="Cart" className="rounded-btn p-2 text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-203" data-mp="33w7niwb2ch08">
                <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" data-id="element-204" data-mp="gjwbqzj4fy3" />
              </button>
              <SiteLink to={routes.location} className="ml-1 inline-flex min-h-[44px] items-center whitespace-nowrap rounded-btn bg-pegasus-blue px-3.5 text-[15px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink xl:px-4" data-id="element-205" data-mp="5f4ttinecliu">
                Visit Showroom
              </SiteLink>
            </div>
          </div>

          <div className="flex items-center gap-1 xl:hidden" data-id="element-206" data-mp="o5uxvaopeeda">
            <button type="button" title="Search" aria-label="Search" className="flex h-11 w-11 items-center justify-center rounded-btn text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-207" data-mp="28t5hp687lm24">
              <SearchIcon className="h-5 w-5" aria-hidden="true" data-id="element-208" data-mp="2dl5p1jqlzh8q" />
            </button>
            <button type="button" title="Cart" aria-label="Cart" className="flex h-11 w-11 items-center justify-center rounded-btn text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-209" data-mp="1pvbcl3a7bup0">
              <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" data-id="element-210" data-mp="1yxc73se1w2mz" />
            </button>
            <button type="button" aria-label="Open menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-btn text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-211" data-mp="1usur84ata9hh">
              <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-212" data-mp="18m0ecg1iq9jz" />
            </button>
          </div>
        </div>

        <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} data-id="element-213" data-mp="1dld7fiizgcv0" />
      </header>
    </>;
}